'use client';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Calendar,
  Clock,
  Users,
  AlertCircle,
  Lock,
  Trophy,
  Cake,
  Network,
} from 'lucide-react';
import {
  TournamentType,
  type Tournament,
  RegistrationStatus,
} from '@/types/tournament.model';
import { formatDateForUser } from '@/utils/formatters';
import { TournamentCountdown } from './TournamentCountdown';
import {
  getTournamentStatus,
  getStatusLabel,
  getStatusColor,
  isTournamentCancelled,
} from '@/utils/tournament-status';
import { getTournamentAgeCompactLabel } from '@/utils/tournament-age';
import { getTournamentFormatLabel } from '@/utils/tournament-format';

interface TournamentCardProps {
  tournament: Tournament;
  onClick: () => void;
}

// Helper function to count pending registrations
const countPendingRegistrations = (tournament: Tournament) => {
  const pendingStatuses = [
    RegistrationStatus.PENDING_PAYMENT,
    RegistrationStatus.PENDING_ADMIN_CHECK,
    RegistrationStatus.PARTIALLY_PAID,
  ];

  return tournament.registrations?.filter((reg) =>
    pendingStatuses.includes(reg.status),
  ).length;
};

// Helper function to count completed registrations
const countCompletedRegistrations = (tournament: Tournament) => {
  return tournament.registrations?.filter(
    (registration) => registration.status === RegistrationStatus.COMPLETE,
  ).length;
};

export function TournamentCard({ tournament, onClick }: TournamentCardProps) {
  const pendingRegistrationsCount = countPendingRegistrations(tournament);
  const isStrictTournament = tournament.type === TournamentType.STRICT;
  const cancelled = isTournamentCancelled(tournament);
  const inPause = tournament.inPause;
  const tournamentStatus = getTournamentStatus(tournament);

  return (
    <Card
      className="relative cursor-pointer overflow-hidden transition-all hover:shadow-lg"
      onClick={onClick}
    >
      <div className="flex flex-col p-4 lg:min-h-[180px] lg:flex-row">
        {/* Image - Full width on mobile, fixed size on desktop */}
        <div className="relative h-40 w-full shrink-0 overflow-hidden rounded-lg bg-white lg:h-full lg:w-72">
          <img
            src={tournament.bannerUrl || '/placeholder.png'}
            alt={tournament.name}
            className={`h-full w-full ${tournament.bannerUrl ? 'object-cover' : 'object-contain'}`}
          />
          {/* Status badge overlay on mobile */}
          <div className="absolute right-2 top-2 flex flex-col items-end gap-1 lg:hidden">
            <Badge
              variant="outline"
              className={`pointer-events-none text-xs font-medium ${getStatusColor(tournamentStatus)}`}
            >
              {getStatusLabel(tournamentStatus)}
            </Badge>
            {isStrictTournament && (
              <div className="rounded-full bg-black/70 p-1">
                <Lock size={12} className="text-white" />
              </div>
            )}
            {!cancelled && inPause && (
              <Badge variant="outline" className="text-xs">
                Pausado
              </Badge>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col pt-3 lg:pl-4 lg:pt-0">
          {/* Header row - Title and status badge */}
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h3 className="line-clamp-3 text-lg font-bold lg:line-clamp-2">
                {tournament.name}
              </h3>
              {/* Status badge - inline with title on desktop */}
              <Badge
                variant="outline"
                className={`pointer-events-none hidden text-xs font-medium lg:inline-flex ${getStatusColor(tournamentStatus)}`}
              >
                {getStatusLabel(tournamentStatus)}
              </Badge>
              {isStrictTournament && (
                <div className="hidden rounded-full bg-black/70 p-1 lg:block">
                  <Lock size={12} className="text-white" />
                </div>
              )}
              {!cancelled && inPause && (
                <Badge
                  variant="outline"
                  className="pointer-events-none hidden text-xs lg:inline-flex"
                >
                  Pausado
                </Badge>
              )}
            </div>
            {/* Location and Sport row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <div className="flex items-center">
                <MapPin size={14} className="mr-1 shrink-0" />
                <span className="line-clamp-1">
                  {tournament.clubs && tournament.clubs.length > 0
                    ? tournament.clubs.map((club) => club.name).join(', ')
                    : 'Club no especificado'}
                </span>
              </div>
              {tournament.sport && (
                <div className="flex items-center">
                  <Trophy size={14} className="mr-1 shrink-0" />
                  <span>{tournament.sport.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Info row - Grid on mobile, flex on desktop */}
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm lg:flex lg:flex-wrap lg:items-start lg:gap-4">
            {/* Tournament format */}
            {tournament.format && (
              <div className="flex items-start gap-2">
                <Network size={16} className="mt-0.5 shrink-0 text-gray-500" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-700">Formato</p>
                  <p className="text-xs text-gray-600">
                    {getTournamentFormatLabel(tournament.format)}
                  </p>
                </div>
              </div>
            )}

            {/* Tournament date */}
            <div className="flex items-start gap-2">
              <Calendar size={16} className="mt-0.5 shrink-0 text-gray-500" />
              <div className="min-w-0">
                <p className="font-medium text-gray-700">Fecha</p>
                <p className="text-xs text-gray-600">
                  {formatDateForUser(tournament.startDate)} -{' '}
                  {formatDateForUser(
                    tournament.endDate || tournament.startDate,
                  )}
                </p>
                {!cancelled && (
                  <div className="mt-0.5 text-xs">
                    <TournamentCountdown
                      signupDeadlineDate={tournament.signupDeadlineDate}
                      startDate={tournament.startDate}
                      endDate={tournament.endDate}
                      endsAt={tournament.endsAt}
                      onlyFor={'tournament'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Registration deadline */}
            <div className="flex items-start gap-2">
              <Clock size={16} className="mt-0.5 shrink-0 text-gray-500" />
              <div className="min-w-0">
                <p className="font-medium text-gray-700">Inscripción</p>
                <p className="text-xs text-gray-600">
                  {formatDateForUser(tournament.signupDeadlineDate)}
                </p>
                {!cancelled && (
                  <div className="mt-0.5 text-xs">
                    <TournamentCountdown
                      signupDeadlineDate={tournament.signupDeadlineDate}
                      startDate={tournament.startDate}
                      endDate={tournament.endDate}
                      endsAt={tournament.endsAt}
                      onlyFor={'registration'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Teams */}
            <div className="flex items-start gap-2">
              <Users size={16} className="mt-0.5 shrink-0 text-gray-500" />
              <div>
                <p className="font-medium text-gray-700">Equipos</p>
                <p className="text-xs text-gray-600">
                  {countCompletedRegistrations(tournament) || 0} /{' '}
                  {tournament.maxTeamsAllowed || 'Sin límite'}
                </p>
              </div>
            </div>

            {/* Age restriction */}
            <div className="flex items-start gap-2">
              <Cake size={16} className="mt-0.5 shrink-0 text-gray-500" />
              <div className="min-w-0">
                <p className="font-medium text-gray-700">Edad</p>
                <p className="text-xs text-gray-600">
                  {getTournamentAgeCompactLabel(
                    tournament.minAge,
                    tournament.maxAge,
                  )}
                </p>
              </div>
            </div>

            {/* Pending registrations (if applicable) */}
            {isStrictTournament && pendingRegistrationsCount > 0 && (
              <div className="flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="mt-0.5 shrink-0 text-amber-500"
                />
                <div>
                  <p className="font-medium text-gray-700">Pendientes</p>
                  <p className="text-xs font-medium text-amber-700">
                    {pendingRegistrationsCount}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
